import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles, withTheme } from '@material-ui/core/styles';

import MainMap from './MainMap';

import SideNavigation from './sidebar/SideNavigation';
import SidebarButton from './sidebar/SidebarButton';

import * as PanelNames from '../../redux/panel/PanelNames';
import {
    PLANNING_ALERTS,
    LAND_ASSEMBLY,
    SEARCH,
    OWNERSHIP_SEARCH,
    DRAWN_SKETCHES,
    NEW_SKETCHES
} from '../../redux/panel/PanelNames';
import {
    hidePrimaryPanel,
    hideSecondaryPanel,
    showPrimaryPanel,
    showSecondaryPanel,
    togglePrimaryPanelCompact,
    updatePanelProperties
} from '../../redux/panel/Actions';

import { PrintIcon } from '../UI/icons';

import AutocompleteSearch from './AutocompleteSearch';

import panelConfigs from './MapPanelConfig';
import { MAIN_MAP } from '../../redux/map/Constants';
import PropertyInfo from './panels/propertyinfo/PropertyInfo';
import PanelContainer from './panels/PanelContainer';
import PrintPanel from './panels/print/PrintPanel';
import Attribution from './Attribution';
import PropertyInfoHeader from './panels/propertyinfo/PropertyInfoHeader';
import BookmarkControl from '../Mapbox/controls/BookmarkControl/Main';
import { withRouter } from 'react-router';
import { fetchBookmarksAsync } from '../../redux/bookmark/Actions';
import { setGlobalPadding } from '../../redux/map/Actions';
import DrawnFeatureInfoBox from './panels/draw/DrawnFeatureInfoBox';

class MapPage extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        // Redux state
        globalPadding: PropTypes.object.isRequired,
        layers: PropTypes.array.isRequired,
        baseLayer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        isAnyPanelExpanded: PropTypes.bool,
        primaryPanel: PropTypes.string,
        secondaryPanel: PropTypes.string,
        currentProjectId: PropTypes.number,
        activeLayers: PropTypes.array,
        features: PropTypes.object.isRequired,
        isMapLoaded: PropTypes.bool.isRequired,
        isDrawing: PropTypes.bool.isRequired,
        // Redux dispatch
        dispatchFetchBookmarksAsync: PropTypes.func.isRequired,
        dispatchSetMapGlobalPadding: PropTypes.func.isRequired,
        dispatchShowPrimaryPanel: PropTypes.func.isRequired,
        dispatchTogglePrimaryPanelCompact: PropTypes.func.isRequired,
        dispatchHidePrimaryPanel: PropTypes.func.isRequired,
        dispatchShowSecondaryPanel: PropTypes.func.isRequired,
        dispatchHideSecondaryPanel: PropTypes.func.isRequired,
        updatePanelProperties: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            printToolShowing: false,
            windowWidth: window.innerWidth
        };

        this.setMapGlobalPadding = this.setMapGlobalPadding.bind(this);
        this.validatePanel = this.validatePanel.bind(this);
        this.createSidebarPanelButton = this.createSidebarPanelButton.bind(this);
        this.handleHelpClick = this.handleHelpClick.bind(this);
        this.changeTool = this.changeTool.bind(this);
        this.buildMapControlContainer = this.buildMapControlContainer.bind(this);
        this.calculatePadding = this.calculatePadding.bind(this);
        this.handleResize = this.handleResize.bind(this);

        props.dispatchFetchBookmarksAsync();
    }

    // didmount and didupdate are the source of how fitboundspadding is set, this padding shouldn't be set anywhere else or risk side effects
    componentDidMount(props) {
        const viewportWidth = window.innerWidth;
        const resolution = this.calculatePadding(viewportWidth);

        window.addEventListener('resize', this.handleResize);
        this.setMapGlobalPadding(resolution.padding);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const resolution = this.calculatePadding(this.state.windowWidth);

        if (this.props.isAnyPanelExpanded !== prevProps.isAnyPanelExpanded) {
            // ToDo - if loading in for first time, update current fitbounds option. Coming form dashboard, fitbounds set without the padding
            this.setMapGlobalPadding(resolution.padding);
        }

        if (resolution.padding !== this.props.globalPadding.top) {
            // Padding is adjusted to prevent crashing on sm/md screens
            this.setMapGlobalPadding(resolution.padding);
        }
    }

    handleResize() {
        // Event watching for window resize
        this.setState({ windowWidth: window.innerWidth });
    }

    calculatePadding(currentWidth) {
        const resolution = {
            padding: 200,
            screenSize: 'lg'
        };

        const smBreakPoint = currentWidth <= 900;
        const mdBreakPoint = currentWidth > 900 && currentWidth < 1200;
        const lgBreakPoint = currentWidth >= 1200;

        if (smBreakPoint) {
            resolution.padding = 0;
            resolution.screenSize = 'sm';
        }

        if (mdBreakPoint) {
            resolution.padding = 100;
            resolution.screenSize = 'md';
        }

        if (lgBreakPoint) {
            resolution.padding = 200;
            resolution.screenSize = 'lg';
        }

        return resolution;
    }

    setMapGlobalPadding(padding) {
        const { isAnyPanelExpanded, theme, dispatchSetMapGlobalPadding } = this.props;

        dispatchSetMapGlobalPadding({
            left: isAnyPanelExpanded ? theme.constants.drawerWidth + padding : padding,
            top: padding,
            bottom: padding,
            right: padding
        });
    }

    componentWillUnmount() {
        // Remove the event listener when the component unmounts
        window.removeEventListener('resize', this.handleResize);
    }

    validatePanel(panelName) {
        const { features } = this.props;

        if (!panelName) {
            return false;
        }

        const config = panelConfigs[panelName];
        if (config) {
            if (
                config.feature &&
                (!features[config.feature] || !features[config.feature].enabled)
            ) {
                return false;
            }
            return true;
        }
        return false;
    }

    createSidebarPanelButton(name, badge) {
        const { title, position, disabled, icon, feature } = panelConfigs[name];
        const { secondaryPanel, features } = this.props;
        return feature && (!features[feature] || !features[feature].enabled) ? (
            <Fragment />
        ) : (
            <SidebarButton
                tooltip
                title={title}
                placement={position}
                disabled={disabled}
                icon={icon}
                onClick={() => this.changeTool(name)}
                active={name == secondaryPanel}
                badge={badge}
            />
        );
    }

    handleHelpClick() {
        window.open('https://www.youtube.com/channel/UCnBitdoIOPJe7xFMWk1doPQ');
    }

    changeTool(panelName) {
        const { secondaryPanel, dispatchShowSecondaryPanel, dispatchHideSecondaryPanel } =
            this.props;
        if (panelName === secondaryPanel) {
            dispatchHideSecondaryPanel();
        } else {
            if (this.validatePanel(panelName)) {
                dispatchShowSecondaryPanel(panelName);
            }
        }
        return;
    }

    buildMapControlContainer = ({ children }) => {
        const { classes, primaryPanel, secondaryPanel } = this.props;
        const panelVisible = primaryPanel || secondaryPanel;
        return (
            <div
                className={classnames(
                    classes.mapOverlay,
                    panelVisible && classes.mapOverlayCompact
                )}>
                {children}
            </div>
        );
    };

    render() {
        const {
            classes,
            primaryPanel,
            secondaryPanel,
            isAnyPanelExpanded,
            isMapLoaded,
            features,
            isDrawing
        } = this.props;
        const { printToolShowing } = this.state;

        const toolConfig = panelConfigs[secondaryPanel];
        const ToolPanel = toolConfig && toolConfig.class;
        const ToolHeader = toolConfig && toolConfig.header;
        // if (location.pathname.split('/')[1] !== 'map' && test_map.mapPosition === null) {
        //     return null;
        // }
        // console.log('test_map', _.cloneDeep(test_map))
        // ${location.pathname.split('/')[1] !== 'map' ? classes.hidden : null}
        return (
            <div className={`${classes.root}`}>
                {/* <div className={`${classes.root} ${location.pathname.split('/')[1] !== 'map' ? classes.hidden : null}`}> */}
                <SideNavigation>
                    {this.createSidebarPanelButton(PanelNames.dataLayers)}
                    {this.createSidebarPanelButton(SEARCH)}
                    {this.createSidebarPanelButton(PLANNING_ALERTS)}
                    {this.createSidebarPanelButton(PanelNames.comparable)}
                    {this.createSidebarPanelButton(LAND_ASSEMBLY)}
                    {this.createSidebarPanelButton(OWNERSHIP_SEARCH)}
                    {features?.drawnFeatures?.enabled
                        ? this.createSidebarPanelButton(DRAWN_SKETCHES)
                        : ''}
                    <SidebarButton
                        tooltip
                        title="Map Export"
                        placement="bottom"
                        icon={<PrintIcon />}
                        onClick={e => this.setState({ printToolShowing: true })}
                    />
                    {/* <SidebarButton */}
                    {/*    tooltip */}
                    {/*    title="Help" */}
                    {/*    placement="bottom" */}
                    {/*    icon={<HelpOutlineIcon/>} */}
                    {/*    onClick={this.handleHelpClick} */}
                    {/* /> */}
                </SideNavigation>
                <main className={classes.content}>
                    <div className={classes.map}>
                        <MainMap
                            ControlContainer={this.buildMapControlContainer}
                            // flyToOptions are currently bugged in raw Mapbox and save the state somehow which is used by fitbounds
                            // Defaulted to 0 padding to resolve
                            // see https://github.com/mapbox/mapbox-gl-js/issues/11831
                            // padding={{
                            //     left: isAnyPanelExpanded ? this.props.theme.constants.drawerWidth : 0
                            // }}
                        ></MainMap>
                    </div>
                    {isMapLoaded && (
                        <div className={classes.left}>
                            <div className={classes.leftTop}>
                                <AutocompleteSearch />
                                <BookmarkControl />
                            </div>
                            {primaryPanel && (
                                <PanelContainer
                                    header={props => <PropertyInfoHeader {...props} />}
                                    isPrimary
                                    test={primaryPanel}>
                                    <PropertyInfo />
                                </PanelContainer>
                            )}
                            {secondaryPanel && (
                                <PanelContainer
                                    header={
                                        toolConfig.header
                                            ? props => <ToolHeader {...props} />
                                            : toolConfig.title
                                    }
                                    icon={toolConfig.icon}
                                    test={secondaryPanel}>
                                    <ToolPanel />
                                </PanelContainer>
                            )}
                            {!(secondaryPanel === NEW_SKETCHES) || isDrawing ? (
                                <DrawnFeatureInfoBox />
                            ) : (
                                ''
                            )}
                            {printToolShowing && (
                                <PrintPanel
                                    open={printToolShowing}
                                    setOpen={val => {
                                        this.setState({ printToolShowing: val });
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {isMapLoaded && <Attribution mapId={MAIN_MAP} />}
                </main>
            </div>
        );
    }
}

const style = theme => ({
    root: {
        display: 'flex',
        // paddingBottom: theme.constants.spacing.md,
        // paddingRight: theme.constants.spacing.md,
        boxSizing: 'border-box'
    },
    hidden: {
        // display: 'none !important'
        visibility: 'hidden'
    },
    content: {
        flexGrow: 1,
        position: 'relative',
        // backgroundColor: theme.palette.background.light,
        borderRadius: '20px'
        // padding: theme.constants.spacing.md,
    },
    left: {
        zIndex: 3,
        padding: theme.constants.spacing.lg,
        width: theme.constants.drawerWidth,
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
        '&> *': {
            pointerEvents: 'all',
            '&:not(:last-child)': {
                marginBottom: theme.constants.spacing.lg
            }
        }
    },
    leftTop: {
        display: 'grid',
        gridTemplateColumns: 'auto 45px',
        columnGap: '20px'
    },
    map: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
        // clipPath: `inset(${theme.constants.spacing.md}px ${theme.constants.spacing.md}px ${theme.constants.spacing.md}px ${theme.constants.spacing.md}px round 10px)`,
        // transition: theme.transitions.create('clip-path', {
        //     easing: theme.transitions.easing.sharp,
        //     duration: theme.transitions.duration.enteringScreen,
        // }),
    },
    mapCompact: {
        // clipPath: `inset(${theme.constants.spacing.md}px ${theme.constants.spacing.md}px ${theme.constants.spacing.md}px ${theme.constants.drawerWidth + (theme.constants.spacing.md * 2)}px round 10px)`,
        // transition: theme.transitions.create('clip-path', {
        //     easing: theme.transitions.easing.sharp,
        //     duration: theme.transitions.duration.leavingScreen,
        // }),
    },
    mapOverlay: {
        zIndex: '3',
        pointerEvents: 'none',
        '& > div': {
            pointerEvents: 'none'
        },
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        margin: `${theme.constants.spacing.lg}px`,
        transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    mapOverlayCompact: {
        left: `${theme.constants.drawerWidth + theme.constants.spacing.lg}px`,
        transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    }
});

const mapStateToProps = state => {
    const panelsState = state.panels;
    // If at least one panel is showing and not compact then need to make changes to focus bounds
    const isPrimaryShowing =
        panelsState.primaryPanel && !panelsState.panels[panelsState.primaryPanel].compact;
    const isSecondaryShowing =
        panelsState.secondaryPanel && !panelsState.panels[panelsState.secondaryPanel].compact;

    return {
        globalPadding: state.mainMap.globalPadding,
        layers: state.layers.layers,
        baseLayer: state.layers.baseLayer,
        primaryPanel: state.panels.primaryPanel,
        secondaryPanel: state.panels.secondaryPanel,
        isAnyPanelExpanded: Boolean(isPrimaryShowing || isSecondaryShowing),
        currentProjectId: state.project.currentProjectId,
        activeLayers: state.activeLayers,
        features: state.user.features,
        isMapLoaded: state[MAIN_MAP].isMapLoaded && Boolean(state[MAIN_MAP].drawModule),
        isDrawing: state[MAIN_MAP].isDrawing
    };
};

const mapDispatchToProps = dispatch => ({
    dispatchFetchBookmarksAsync: () => dispatch(fetchBookmarksAsync()),
    dispatchSetMapGlobalPadding: padding => dispatch(setGlobalPadding(padding, MAIN_MAP)),
    dispatchShowPrimaryPanel: name => dispatch(showPrimaryPanel(name)),
    dispatchTogglePrimaryPanelCompact: () => dispatch(togglePrimaryPanelCompact()),
    dispatchHidePrimaryPanel: () => dispatch(hidePrimaryPanel()),
    dispatchShowSecondaryPanel: name => dispatch(showSecondaryPanel(name)),
    dispatchHideSecondaryPanel: () => dispatch(hideSecondaryPanel()),
    updatePanelProperties: (name, properties) => dispatch(updatePanelProperties(name, properties))
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(style),
    withTheme,
    withRouter
)(MapPage);
